import { useEffect, useRef, useState } from "react";

import { cn } from "../../handlers/cn.utils";

interface SearchableSelectInputProps {
	options?: { label: string; value: any }[];
	onSelect: (value: any) => void;
	value?: any;
	defaultLabel?: string;
	/** Optional async source, used only when the static options yield no match. */
	fetchOptions?: (searchTerm: string) => Promise<{ label: string; value: any }[]>;
	debounce?: number;
}

const SearchableSelectInput = ({
	options = [],
	onSelect,
	value,
	defaultLabel,
	fetchOptions,
	debounce = 500,
}: SearchableSelectInputProps) => {
	const [searchTerm, setSearchTerm] = useState("");
	const [isOpen, setIsOpen] = useState(false);
	const [fetchedOptions, setFetchedOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [error, setError] = useState(null);

	const dropdownRef = useRef(null);
	const debounceRef = useRef(null);

	// Always filter locally first
	const filteredStaticOptions = options.filter((option) =>
		option.label.toLowerCase().includes(searchTerm.toLowerCase()),
	);

	// What we display: static matches > API results
	const mergedOptions = filteredStaticOptions.length > 0 ? filteredStaticOptions : fetchedOptions;

	const toggleDropdown = () => setIsOpen((prev) => !prev);

	const handleClickOutside = (event) => {
		if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
			setIsOpen(false);
		}
	};

	useEffect(() => {
		if (isOpen) {
			document.addEventListener("mousedown", handleClickOutside);
		} else {
			document.removeEventListener("mousedown", handleClickOutside);
		}
		return () => {
			document.removeEventListener("mousedown", handleClickOutside);
		};
	}, [isOpen]);

	// Hybrid fetching: only call API if local has no matches
	useEffect(() => {
		if (!fetchOptions) return;
		if (debounceRef.current) clearTimeout(debounceRef.current);

		if (searchTerm.trim() === "") {
			setFetchedOptions([]);
			setError(null);
			setLoading(false);
			return;
		}

		// Only hit API if no local matches
		if (options.filter((option) => option.label.toLowerCase().includes(searchTerm.toLowerCase())).length === 0) {
			debounceRef.current = setTimeout(async () => {
				try {
					setLoading(true);
					setError(null);
					const data = await fetchOptions(searchTerm);
					setFetchedOptions(Array.isArray(data) ? data : []);
				} catch (err) {
					setError(err instanceof Error ? err.message : "Failed to fetch");
					setFetchedOptions([]);
				} finally {
					setLoading(false);
				}
			}, debounce);
		} else {
			// reset remote results if local is available
			setFetchedOptions([]);
		}

		return () => clearTimeout(debounceRef.current);
	}, [searchTerm, fetchOptions, debounce, options]);

	return (
		<div ref={dropdownRef} className="relative w-full">
			<div
				onClick={toggleDropdown}
				className="border cursor-pointer border-gray-200 flex justify-between items-center h-9 text-sm placeholder:text-sm rounded px-2 py-1 placeholder:text-[#7A7A7A]"
				style={{ padding: 8 }}
			>
				{value
					? mergedOptions.find((item) => item.value === value)?.label || defaultLabel || ""
					: defaultLabel || "Select an option"}
				<i className="bi bi-chevron-down ml-5" />
			</div>

			{isOpen && (
				<div className="absolute mt-1 rounded-md bg-white shadow-lg border border-gray-400 w-full">
					<input
						type="text"
						value={searchTerm}
						onChange={(e) => setSearchTerm(e.target.value)}
						className="w-full border rounded-md border-b-0 border-r-0 border-l-0 p-2 outline-hidden focus:ring-0"
						placeholder="Search..."
					/>

					<ul className="max-h-60 overflow-y-auto">
						{loading && <li className="p-2 text-gray-600">Loading...</li>}
						{error && <li className="p-2 text-red-500">{error}</li>}

						{!loading && !error && mergedOptions.length > 0
							? mergedOptions.map((option, index) => (
									<li
										key={index}
										className={cn("cursor-pointer p-2 text-left hover:bg-gray-200", {
											"text-gray-200": option.disabled,
											"text-gray-900": !option.disabled,
											"bg-gray-100": value === option.value,
										})}
										onClick={() => {
											onSelect(option.value);
											setSearchTerm("");
											setIsOpen(false);
										}}
									>
										<span className="d-flex items-center space-x-2">
											{value === option.value && <span className="bi bi-check-circle-fill mr-2 text-gray-950" />}
											{option.icon && <span className={`bi bi-${option.icon} mx-2`} />}
											<span className="flex flex-col">
												<span className="text-sm">{option.label}</span>
												<span className="text-sm italic text-gray-500">{option.subLabel}</span>
											</span>
										</span>
									</li>
								))
							: !loading && !error && <li className="p-2 text-gray-600">No results found</li>}
					</ul>
				</div>
			)}
		</div>
	);
};

export default SearchableSelectInput;
